import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'
import { getDiff } from '@/api/diff'
import { getDraftFile } from '@/api/draftFiles'
import { getDraftPrompts, normalizeFilename, type DraftPromptListEntry } from '@/api/prompts'
import { Banner } from '@/components/Banner'
import { DataTable, type DataTableColumn } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { errorMessage } from '@/lib/apiErrors'
import { refreshAfterWrite } from '@/lib/refreshAfterWrite'
import type { DocumentTypesFile, ExtractorsFile, ProductTypesFile } from '@/types/bundle'
import { PromptFileRow } from './components/PromptFileRow'
import { getPromptDeleteState } from './promptDeleteState'

function SkeletonBlock() {
  return <div className="h-64 animate-pulse rounded-control bg-surface-raised" />
}

// A direct browser/editor over every real file in the bundle's prompts/
// folder (PRD 29's raw file-listing endpoint), searchable by filename or by
// what it's used by. This is additive to the existing embedded editors -
// Product Types' Extraction section still has its own Baseline Prompt field
// and Document Types still has its own Prompt tab, unchanged; this screen
// is a separate, S3-folder-level view over the same underlying files.
export function PromptsScreen() {
  const queryClient = useQueryClient()
  const promptsListQuery = useQuery({
    queryKey: ['draft-prompts'],
    queryFn: getDraftPrompts,
  })
  const productTypesQuery = useQuery({
    queryKey: ['draft-file', 'product_types'],
    queryFn: () => getDraftFile<ProductTypesFile>('product_types'),
  })
  const documentTypesQuery = useQuery({
    queryKey: ['draft-file', 'document_types'],
    queryFn: () => getDraftFile<DocumentTypesFile>('document_types'),
  })
  const extractorsQuery = useQuery({
    queryKey: ['draft-file', 'extractors'],
    queryFn: () => getDraftFile<ExtractorsFile>('extractors'),
  })
  // Same query (and cache) as Diff & Validation's default live-vs-draft view
  // - only used to work out each row's delete state, so it never blocks or
  // fails the screen: while it loads or if it fails, every row's delete
  // button is just disabled ('unknown').
  const diffQuery = useQuery({ queryKey: ['diff', 'live', 'draft'], queryFn: () => getDiff('live', 'draft') })

  const [expandedFilename, setExpandedFilename] = useState<string | undefined>(undefined)
  const [search, setSearch] = useState('')

  if (
    promptsListQuery.isPending ||
    productTypesQuery.isPending ||
    documentTypesQuery.isPending ||
    extractorsQuery.isPending
  ) {
    return <SkeletonBlock />
  }

  if (promptsListQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load the prompts folder">
        {errorMessage(promptsListQuery.error)}
      </Banner>
    )
  }
  if (productTypesQuery.isError || documentTypesQuery.isError || extractorsQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load product/document context">
        {errorMessage(productTypesQuery.error) ?? errorMessage(documentTypesQuery.error) ?? errorMessage(extractorsQuery.error)}
      </Banner>
    )
  }

  // "Used by" is informational only, computed from whatever already
  // references this filename - it never decides which files appear in the
  // table (every real file in prompts/ appears, referenced or not).
  function describeUsage(filename: string): string {
    const normalized = normalizeFilename(filename)
    for (const [productId, product] of Object.entries(productTypesQuery.data!.product_types)) {
      if (product.extraction?.prompt_template_file && normalizeFilename(product.extraction.prompt_template_file) === normalized) {
        return `Product baseline: ${productId}`
      }
    }
    for (const [documentTypeId, entry] of Object.entries(documentTypesQuery.data!.document_types)) {
      if (entry.prompt?.template_file && normalizeFilename(entry.prompt.template_file) === normalized) {
        return `Document prompt: ${documentTypeId} (${entry.prompt.mode})`
      }
    }
    for (const [extractorId, extractor] of Object.entries(extractorsQuery.data!.extractors)) {
      if (extractor.prompt_template_file && normalizeFilename(extractor.prompt_template_file) === normalized) {
        return `Extractor default: ${extractorId}`
      }
    }
    return 'Unreferenced'
  }

  const searchLower = search.trim().toLowerCase()
  const rows = promptsListQuery.data
    .map((entry) => ({ entry, usedBy: describeUsage(entry.filename) }))
    .filter(
      ({ entry, usedBy }) =>
        searchLower.length === 0 ||
        entry.filename.toLowerCase().includes(searchLower) ||
        usedBy.toLowerCase().includes(searchLower),
    )
    .sort((a, b) => a.entry.filename.localeCompare(b.entry.filename))

  const columns: DataTableColumn<{ entry: DraftPromptListEntry; usedBy: string }>[] = [
    {
      key: 'filename',
      header: 'File',
      render: ({ entry }) => (
        <button
          type="button"
          onClick={() => setExpandedFilename((current) => (current === entry.filename ? undefined : entry.filename))}
          className="text-left font-medium text-accent hover:underline"
        >
          {entry.filename}
        </button>
      ),
    },
    {
      key: 'usedBy',
      header: 'Used by',
      render: ({ usedBy }) => (usedBy === 'Unreferenced' ? <span className="text-text-disabled">Unreferenced</span> : usedBy),
    },
    {
      key: 'status',
      header: 'Status',
      render: ({ entry }) => (entry.existsInDraft ? 'Draft edit' : 'Published'),
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-title font-semibold text-text-primary">Prompts</h1>

      <div className="flex flex-col gap-1">
        <label className="text-meta font-medium text-text-secondary" htmlFor="prompts-search">
          Search
        </label>
        <input
          id="prompts-search"
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Filename or product/document…"
          className="w-72 rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
        />
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={AlertCircle}
          headline={search ? 'No prompt files match your search' : 'No prompt files yet'}
          description={
            search
              ? 'Try a different filename or clear the search.'
              : 'Once a Baseline Prompt or Document Prompt is saved somewhere, its file will show up here.'
          }
          action={search ? { label: 'Clear search', onClick: () => setSearch('') } : undefined}
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowKey={({ entry }) => entry.filename}
          expandedRowKey={expandedFilename}
          renderExpanded={({ entry, usedBy }) => (
            <PromptFileRow
              filename={entry.filename}
              usedBy={usedBy}
              isReferenced={usedBy !== 'Unreferenced'}
              deleteState={getPromptDeleteState(entry.filename, diffQuery.data)}
              onChanged={() => refreshAfterWrite(queryClient, { kind: 'prompt-file', filename: entry.filename })}
            />
          )}
        />
      )}
    </div>
  )
}
